import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { actionContextForDeskMode, dispatchCheckedItemAction, itemActionsForContext, type ItemActionContext, type ItemActionId } from '@/msgdeskRuntime/itemMovements'
import { selectCheckedSubIds, selectFavsAllowed } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function stop(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export function PooFoldSubsButton({
  section = 0,
  label,
  count,
}: {
  section?: 0 | 1
  label: string
  count?: number
}) {
  const contract: LegacyContractName = section === 0 ? 'subs.menuToggle.answers' : 'subs.menuToggle.submsges'

  function handleFold(event: MouseEvent<HTMLElement>) {
    stop(event)
    dispatchSubsDisclosure({ action: 'toggle-section', section, target: event.currentTarget })
  }

  return (
    <a
      href="#"
      {...legacyHookAttrs(contract, 'md-fold-subs md-fold-subs--poo float-left mr-[5px] mb-0.5 flex cursor-pointer items-center bg-green-700 p-0.5 text-[10px] text-white no-underline')}
      onClick={handleFold}
    >
      <span>{label}</span>
      {typeof count === 'number' ? <span className="ml-0.5">({count})</span> : null}
    </a>
  )
}

export function PooPagingBar({
  section,
  currentPage = 1,
  totalPages = 1,
  registerContract = true,
}: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const paging = createPagingModel(currentPage, totalPages)
  const pageClass = 'md-paging__page inline-flex h-5 min-w-5 items-center justify-center border border-[#ccc] bg-white px-1 text-[11px] text-[#3472a9] no-underline hover:bg-[#edf4fa]'

  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stop(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  const attrs = registerContract
    ? legacyHookAttrs(contract, 'md-paging md-paging--poo clear-both mb-1 flex items-center gap-0.5')
    : { className: 'md-paging md-paging--poo clear-both mb-1 flex items-center gap-0.5' }

  return (
    <div {...attrs}>
      {paging.tokens.map((token, index) => token === 'gap' ? (
        <span key={`gap-${index}`} className="px-1 text-[#888]">…</span>
      ) : (
        <a
          key={token}
          href="#"
          className={`${pageClass}${token === paging.currentPage ? ' bg-[#3472a9] text-white' : ''}`}
          onClick={event => pageClick(event, token)}
        >
          {token}
        </a>
      ))}
      {/* Edge buttons hide at the first/last page (kept mounted for parity
          with the other surfaces' index-based sequence contract). */}
      <a href="#" className={`${pageClass}${paging.previousDisabled ? ' opacity-40' : ''}`} style={paging.previousDisabled ? { display: 'none' } : undefined} onClick={event => pageClick(event, 'previous')} aria-label="Previous page">‹</a>
      <a href="#" className={`${pageClass}${paging.nextDisabled ? ' opacity-40' : ''}`} style={paging.nextDisabled ? { display: 'none' } : undefined} onClick={event => pageClick(event, 'next')} aria-label="Next page">›</a>
    </div>
  )
}

export function PooRefreshSubs({ section }: { section: 0 | 1 }) {
  return (
    <button
      type="button"
      className="md-refresh-subs md-refresh-subs--poo mr-1 h-5 border border-[#ccc] bg-white px-1.5 text-[11px] text-[#3472a9] hover:bg-[#edf4fa]"
      title="Refresh"
      onClick={event => dispatchSubsNavigation({ action: 'refresh', section, target: event.currentTarget })}
    >
      Refresh
    </button>
  )
}

type CheckedsMode = 'msg' | 'trash' | 'drafts' | 'favs'

const actionLabels: Readonly<Record<ItemActionContext, Partial<Record<ItemActionId, string>>>> = {
  active: { 'move-to-trash': 'Delete', 'move-to-drafts': 'In Drafts', 'add-favorite': 'Favorite' },
  drafts: { 'move-to-trash': 'Delete', 'restore-to-active': 'Create' },
  trash: { 'restore-to-active': 'Restore' },
  favorites: { 'remove-favorite': 'Unfavorite' },
}


export function PooCheckedsInfo({
  section,
  mode = 'msg',
}: {
  section: 0 | 1
  mode?: CheckedsMode
  visual?: 'modern' | 'legacy'
}) {
  const { state } = useSubsStore()
  const context = actionContextForDeskMode(mode)
  // FAVS-IDENTITY: bulk favorite actions hide with the rest of the favs UI.
  const favsAllowed = selectFavsAllowed(state)
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : state.renderProjection.mode
  const checkedIds = selectCheckedSubIds(state, sectionKey, checkedMode)
  const checkedSet = new Set(checkedIds)
  const currentIds = state.renderProjection.rootIds[sectionKey] || []
  const currentCount = currentIds.filter(id => checkedSet.has(id)).length
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'

  function runAction(event: MouseEvent<HTMLElement>, action: ItemActionId) {
    void dispatchCheckedItemAction({ action, context, section, target: event.currentTarget })
  }

  return (
    <div {...legacyContractDataAttrs(contract)} className="md-checked-info md-checked-info--poo inline-flex min-h-5 items-center gap-1 text-[11px] text-[#555]">
      <button type="button" className="h-5 border border-[#ccc] bg-white px-1 text-[#3472a9]" onClick={event => dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })}>
        ✓ {currentCount || ''}{checkedIds.length ? ` (${checkedIds.length})` : ''}
      </button>
      {itemActionsForContext(context).filter(action => favsAllowed || (action !== 'add-favorite' && action !== 'remove-favorite')).map(action => actionLabels[context][action] ? (
        <button key={action} type="button" className="h-5 border border-[#ccc] bg-white px-1 text-[#3472a9]" data-md-item-action={action} onClick={event => runAction(event, action)}>
          {actionLabels[context][action]}
        </button>
      ) : null)}
    </div>
  )
}
